import { useEffect, useRef, useState } from 'react'
import type { Site } from '../lib/api'
import { api } from '../lib/api'
import { Modal, Button, Spinner } from './ui'

export function SiteLogsModal({ site, onClose }: { site: Site; onClose: () => void }) {
  const [tab, setTab] = useState<'access' | 'error'>('access')
  const [live, setLive] = useState(true)
  const [lines, setLines] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [path, setPath] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)

  // history load on switch tab
  useEffect(() => {
    setLoading(true); setLines([])
    api.get<{ path: string; text: string }>(`/sites/${site.id}/logs?type=${tab}&tail=200`)
      .then(r => {
        setPath(r.data.path)
        setLines(r.data.text ? r.data.text.split('\n').filter(Boolean) : [])
      })
      .catch(() => setLines(['— не удалось прочитать —']))
      .finally(() => setLoading(false))
  }, [tab, site.id])

  // live-tail через SSE
  useEffect(() => {
    if (!live) return
    const token = localStorage.getItem('beebro.token') || ''
    // SSE не поддерживает Authorization header — пробрасываем query
    const es = new EventSource(`/api/v1/sites/${site.id}/logs/stream?type=${tab}&token=${encodeURIComponent(token)}`)
    es.addEventListener('log', (ev: MessageEvent) => {
      try {
        const line = JSON.parse((ev as any).data)
        setLines((prev) => {
          const next = [...prev, line]
          return next.length > 2000 ? next.slice(-2000) : next
        })
      } catch { /* ignore */ }
    })
    es.onerror = () => { /* автопереподключается */ }
    return () => es.close()
  }, [tab, live, site.id])

  // autoscroll вниз
  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight
  }, [lines])

  return (
    <Modal open onClose={onClose} title={`Логи: ${site.domain}`} size="lg">
      <div className="flex items-center gap-2 mb-3">
        <button
          className={`px-3 py-1 rounded-md text-[13px] font-medium ${tab === 'access' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700'}`}
          onClick={() => setTab('access')}
        >access.log</button>
        <button
          className={`px-3 py-1 rounded-md text-[13px] font-medium ${tab === 'error' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700'}`}
          onClick={() => setTab('error')}
        >error.log</button>
        <label className="ml-auto text-[12px] text-neutral-600 flex items-center gap-1 select-none cursor-pointer">
          <input type="checkbox" checked={live} onChange={e => setLive(e.target.checked)} />
          live-tail
          {live && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse ml-1" />}
        </label>
        <Button size="sm" variant="secondary" onClick={() => setLines([])}>Очистить</Button>
      </div>
      {path && <div className="text-[11px] text-neutral-500 font-mono mb-2">{path}</div>}
      <div ref={boxRef} className="bg-neutral-900 text-neutral-100 text-[11px] font-mono p-3 rounded-md h-[500px] overflow-y-auto whitespace-pre-wrap break-all">
        {loading && <Spinner />}
        {!loading && lines.length === 0 && <div className="text-neutral-500">— пусто —</div>}
        {lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
      <div className="flex justify-end pt-3 border-t border-neutral-100 mt-3">
        <Button variant="ghost" onClick={onClose}>Закрыть</Button>
      </div>
    </Modal>
  )
}
